import { UseQueryOptions } from '@tanstack/react-query';
import { GraphQLClient, gql } from 'graphql-request';

import { GraphQLSupporter } from '@sola/schema/dotcom/supporters/schema.generated.js';

const graphqlEndpoint =
  process.env.GRAPHQL_PROXY_DESTINATION ||
  `${process.env.NEXT_PUBLIC_BASE_URL_PROXY_API}/graphql`;

/**
 * GraphQL Client
 * If needbe, we can configure error handling in the middleware.
 */
const client = new GraphQLClient(graphqlEndpoint, {
  credentials: 'include',
});

/**
 * Response from a graphql query.
 * It allows us to specify the data being returned, which is generally recommended.
 */
type SupporterResponse<
  TKey extends keyof GraphQLSupporter | undefined = undefined,
> = {
  supporter: TKey extends keyof GraphQLSupporter
    ? Pick<GraphQLSupporter, TKey>
    : Pick<GraphQLSupporter, keyof GraphQLSupporter>;
  // null is to handle errors, double check this.
} | null;

export type SupporterQuery = Omit<UseQueryOptions, 'queryFn'> & {
  queryFn: (globalId: string) => Promise<Partial<GraphQLSupporter>>;
};

/**
 * Get all payment method information.
 */
export const getPaymentMethods = async (globalId: string) => {
  const response: SupporterResponse<'paymentMethods'> = await client.request(
    gql`
      query GetSupporterPaymentMethods($globalId: String!) {
        supporter(globalId: $globalId) {
          paymentMethods {
            id
            status
            accountNickname
            billingAddress {
              attention
              street
              city
              stateCode
              postalCode
              country
            }
            paymentType
            isPrimary
            creditCard {
              cardholderName
              cardType
              digitalType
              expirationDate
              expiryStatus
              lastFour
              responseCode
            }
            ach {
              accountName
              lastFourOfAccountNumber
              routingNumber
              token
            }
          }
        }
      }
    `,
    { globalId },
  );
  return response?.supporter ?? {};
};

/**
 * Query all payment methods.
 * // TODO, clean up TS once we get things going.
 */
export const paymentMethodsQuery: SupporterQuery = {
  queryKey: ['supporterPaymentMethods'],
  queryFn: (globalId) => getPaymentMethods(globalId),
};

/**
 * Get all commitments.
 *
 * // TODO possibly reduce to what we know we actually need.
 */
export const getCommitments = async (globalId: string) => {
  const response: SupporterResponse<'commitments'> = await client.request(
    gql`
      query GetSupporterCommitments($globalId: String!) {
        supporter(globalId: $globalId) {
          commitments {
            id
            participant {
              birthdate
              country
              gender
              globalId
              localId
              name
              photoUrl
            }
            commitmentType
            dates {
              start
              end
            }
            fund {
              code
              description
              icon
            }
            internalId
            interventionId
            isCorrespondent
            isRecurring
            lastCorrespondenceDate
            paymentFrequency
            paymentMethodId
            paymentPullDay
            rate
            receiptorId
            sourceCode
            status
            transactionId
          }
        }
      }
    `,
    { globalId },
  );
  return response?.supporter ?? {};
};

/**
 * Query all commitment data
 * // TODO, clean up TS once we get things going.
 */
export const commitmentsQuery: SupporterQuery = {
  queryKey: ['supporterCommitments'],
  queryFn: (globalId) => getCommitments(globalId),
};
